<template>
  <div class="view-pricelistproducts">
    <PageHeader :breadcrumbs="breadcrumbs" icon="mdi-notebook" />

    <v-container fluid>
      <!-- Info listino -->
      <v-row>
        <v-col>
          <p class="text-subtitle-1 grey--text mb-4">
            {{ $t('pricelists.products.subtitle', { pricelist: pricelistName }) }}
          </p>
          <v-alert
            v-if="isDerivedPricelist && basePricelist"
            type="info"
            dense
            outlined
            class="mb-4"
          >
            {{ $t('pricelists.products.derivedInfo', { basePricelist: basePricelist.name }) }}
          </v-alert>
        </v-col>
      </v-row>

      <!-- Card principale -->
      <v-row>
        <v-col>
          <v-card>
            <!-- Filtri -->
            <v-card-title>
              <v-row no-gutters>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="filters.search"
                    :placeholder="$t('pricelists.products.searchPlaceholder')"
                    append-icon="mdi-magnify"
                    clearable
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6" class="pl-md-2">
                  <v-autocomplete
                    v-model="filters.phase"
                    :items="phasesWithLocalizedNames"
                    item-text="localizedName"
                    item-value="id"
                    :placeholder="$t('pricelists.products.allPhases')"
                    clearable
                    outlined
                    dense
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Data table -->
            <v-data-table
              :headers="headers"
              :items="filteredProducts"
              :items-per-page="15"
              :loading="loading"
              item-key="id"
              :item-class="getItemClass"
            >
              <!-- Checkbox disponibilità -->
              <template v-slot:[`item.available`]="{ item }">
                <v-checkbox
                  :input-value="isProductAvailable(item.id)"
                  @change="toggleProduct(item.id)"
                  hide-details
                  class="mt-0"
                ></v-checkbox>
              </template>

              <!-- Nome prodotto con feedback visivo -->
              <template v-slot:[`item.name`]="{ item }">
                <span :class="{ 'grey--text text--lighten-1': !isProductAvailable(item.id) }">
                  {{ getLocalizedProductName(item, userLanguage) }}
                </span>
              </template>

              <!-- Fase con feedback visivo -->
              <template v-slot:[`item.phase`]="{ item }">
                <span :class="{ 'grey--text text--lighten-1': !isProductAvailable(item.id) }">
                  <ProductPhases :product="item" />
                </span>
              </template>

              <!-- Colonna Fascia Prezzo -->
              <template v-slot:[`item.priceRange`]="{ item }">
                <div
                  v-for="(row, index) in getProductPriceRanges(item)"
                  :key="index"
                >
                  <!-- Separatore tra fasce diverse -->
                  <div
                    v-if="row.isFirstInGroup && index > 0"
                    class="price-range-separator"
                  ></div>
                  <div
                    class="d-flex align-start"
                    :class="{
                      'grey--text text--lighten-1': !isProductAvailable(item.id),
                      'py-1': true,
                      'pt-2': row.isFirstInGroup,
                    }"
                  >
                    <span
                      class="flex-grow-1 price-range-name"
                      :class="{ 'align-self-start': true }"
                      :title="row.priceRangeName"
                    >
                      {{ row.priceRangeName }}
                    </span>
                    <v-btn
                      v-if="row.isFirstRowInRange && !isDerivedPricelist"
                      icon
                      x-small
                      @click="onEditPrices(item, row.priceRangeIndex)"
                      class="ml-2 align-self-start"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </div>
                </div>
              </template>

              <!-- Colonna Packaging -->
              <template v-slot:[`item.packaging`]="{ item }">
                <div
                  v-for="(row, index) in getProductPriceRanges(item)"
                  :key="index"
                >
                  <!-- Separatore tra fasce diverse -->
                  <div
                    v-if="row.isFirstInGroup && index > 0"
                    class="price-range-separator"
                  ></div>
                  <div
                    :class="{
                      'grey--text text--lighten-1': !isProductAvailable(item.id) || !isPackagingAvailable(row.price),
                      'py-1': true,
                      'pt-2': row.isFirstInGroup,
                    }"
                  >
                    {{ formatPackaging(row.packaging, item.um) }}
                  </div>
                </div>
              </template>

              <!-- Colonna Prezzi -->
              <template v-slot:[`item.prices`]="{ item }">
                <div
                  v-for="(row, index) in getProductPriceRanges(item)"
                  :key="index"
                >
                  <!-- Separatore tra fasce diverse -->
                  <div
                    v-if="row.isFirstInGroup && index > 0"
                    class="price-range-separator"
                  ></div>
                  <div
                    :class="{
                      'grey--text text--lighten-1': !isProductAvailable(item.id) || !isPackagingAvailable(row.price),
                      'py-1': true,
                      'pt-2': row.isFirstInGroup,
                      'font-weight-medium': isPackagingAvailable(row.price)
                    }"
                  >
                    {{ formatPrice(row.price) }}
                  </div>
                </div>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Dialog modifica prezzi -->
    <EditPriceRangePricesDialog
      v-model="editPricesDialog"
      :product="selectedProduct"
      :price-range-index="selectedPriceRangeIndex"
      :pricelist-id="pricelistId"
      @save="onSavePrices"
    />
  </div>
</template>

<script>
import firebase from 'firebase';
import PageHeader from '@/components/PageHeader';
import ProductPhases from '@/components/ProductPhases';
import EditPriceRangePricesDialog from '@/components/EditPriceRangePricesDialog';
import { getLocalizedProductName, getLocalizedPriceRangeName } from '@/utils/i18n-helpers';
import { getPriceForPricelist } from '@/utils/pricelists';

export default {
  name: 'PricelistProducts',

  components: {
    PageHeader,
    ProductPhases,
    EditPriceRangePricesDialog,
  },

  data: () => ({
    filters: {
      search: '',
      phase: null,
    },
    availableProductIds: [], // Array di ID prodotti disponibili
    loading: true,
    pricelist: null,
    saveTimeout: null, // Per debounce
    editPricesDialog: false,
    selectedProduct: null,
    selectedPriceRangeIndex: null,
  }),

  async created() {
    await this.loadPricelist();
    await this.$store.dispatch('products/INIT_PRODUCTS');
    await this.$store.dispatch('phases/INIT_PHASES');
    this.loading = false;
  },

  computed: {
    pricelistId() {
      return this.$route.params.id;
    },

    pricelistName() {
      return this.pricelist?.name || '';
    },

    userLanguage() {
      return this.$i18n.locale || 'it';
    },

    products() {
      return this.$store.getters['products/docs'];
    },

    phases() {
      return this.$store.getters['phases/docs'];
    },

    phasesWithLocalizedNames() {
      return this.phases.map((phase) => ({
        ...phase,
        localizedName: this.getLocalizedPhaseName(phase),
      }));
    },

    filteredProducts() {
      let productsToFilter = this.products;

      // Per listini derivati: mostra solo prodotti disponibili nel base
      if (this.isDerivedPricelist && this.basePricelist) {
        const baseProductIds = this.basePricelist.availableProducts || [];
        productsToFilter = productsToFilter.filter((p) => baseProductIds.includes(p.id));
      }

      return productsToFilter.filter((p) => {
        // Escludi archiviati
        if (p.archived) return false;

        // Filtro ricerca
        if (this.filters.search && this.filters.search.trim()) {
          const searchLower = this.filters.search.toLowerCase();
          const productName = getLocalizedProductName(p, this.userLanguage).toLowerCase();
          if (!productName.includes(searchLower)) return false;
        }

        // Filtro fase
        if (this.filters.phase && !p.phases?.hasOwnProperty(this.filters.phase)) {
          return false;
        }

        return true;
      });
    },

    isDerivedPricelist() {
      return this.pricelist?.pricelistType === 'derived';
    },

    basePricelist() {
      if (!this.isDerivedPricelist || !this.pricelist?.basePricelistId) return null;
      return this.$store.getters['pricelists/docs'].find(
        (p) => p.id === this.pricelist.basePricelistId,
      );
    },

    allPricelists() {
      return this.$store.getters['pricelists/docs'];
    },

    headers() {
      return [
        {
          text: this.$t('pricelists.products.headers.available'),
          value: 'available',
          sortable: false,
          width: '100px',
        },
        {
          text: this.$t('pricelists.products.headers.productName'),
          value: 'name',
          sortable: false,
        },
        {
          text: this.$t('pricelists.products.headers.phase'),
          value: 'phase',
          sortable: false,
        },
        {
          text: this.$t('pricelists.products.headers.priceRange'),
          value: 'priceRange',
          sortable: false,
          width: '180px',
        },
        {
          text: this.$t('pricelists.products.headers.packaging'),
          value: 'packaging',
          sortable: false,
          width: '120px',
        },
        {
          text: this.$t('pricelists.products.headers.prices'),
          value: 'prices',
          sortable: false,
          width: '120px',
        },
      ];
    },

    breadcrumbs() {
      return [
        {
          text: this.$t('pricelists.products.breadcrumbs.pricelists'),
          to: { name: 'Listini' },
          exact: true,
        },
        {
          text: this.$t('pricelists.products.title'),
          exact: true,
        },
      ];
    },
  },

  methods: {
    getLocalizedProductName,
    getLocalizedPriceRangeName,

    async loadPricelist() {
      const db = firebase.firestore();
      const doc = await db.collection('pricelists').doc(this.pricelistId).get();

      if (doc.exists) {
        this.pricelist = { id: doc.id, ...doc.data() };
        // Inizializza array disponibili (default vuoto se non esiste)
        this.availableProductIds = this.pricelist.availableProducts || [];
      }
    },

    isProductAvailable(productId) {
      return this.availableProductIds.includes(productId);
    },

    isPackagingAvailable(price) {
      // Un packaging è considerato disponibile solo se ha un prezzo valido (> 0)
      return price !== null && price !== undefined && price > 0;
    },

    toggleProduct(productId) {
      if (this.isProductAvailable(productId)) {
        // Rimuovi (sempre permesso)
        this.availableProductIds = this.availableProductIds.filter((id) => id !== productId);
        this.debouncedSave();
      } else {
        // Aggiungi: solo se non è un listino derivato o se il prodotto è nel base
        if (this.isDerivedPricelist && this.basePricelist) {
          const baseProductIds = this.basePricelist.availableProducts || [];
          if (!baseProductIds.includes(productId)) {
            // Non permettere aggiunta di prodotti non presenti nel base
            return;
          }
        }
        // Aggiungi
        this.availableProductIds = [...this.availableProductIds, productId];
        this.debouncedSave();
      }
    },

    debouncedSave() {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = setTimeout(() => {
        this.saveAvailableProducts();
      }, 500);
    },

    async saveAvailableProducts() {
      try {
        await this.$store.dispatch('pricelists/setAvailableProducts', {
          pricelistId: this.pricelistId,
          productIds: this.availableProductIds,
        });
      } catch (error) {
        console.error('Errore salvataggio prodotti disponibili:', error); // eslint-disable-line no-console
      }
    },

    getItemClass(item) {
      return this.isProductAvailable(item.id) ? '' : 'grey lighten-4';
    },

    getLocalizedPhaseName(phase) {
      if (typeof phase.name === 'string') return phase.name;
      if (typeof phase.name === 'object') {
        return phase.name[this.userLanguage] || phase.name.it || 'Fase senza nome';
      }
      return 'Fase senza nome';
    },

    onEditPrices(product, priceRangeIndex) {
      this.selectedProduct = product;
      this.selectedPriceRangeIndex = priceRangeIndex;
      this.editPricesDialog = true;
    },

    // eslint-disable-next-line object-curly-newline
    async onSavePrices({ productId, priceRangeIndex, pricelistId, prices }) {
      try {
        const db = firebase.firestore();
        const productRef = db.collection('products').doc(productId);

        // Recupera il prodotto corrente
        const productDoc = await productRef.get();
        if (!productDoc.exists) {
          throw new Error('Prodotto non trovato');
        }

        const productData = productDoc.data();

        // Aggiorna i prezzi nella fascia di prezzo specificata
        const updatedPriceRange = { ...productData.priceranges[priceRangeIndex] };

        // Inizializza prices come array vuoto se non esiste
        const existingPrices = updatedPriceRange.prices || [];

        // Sostituisce completamente l'array dei prezzi con quello aggiornato
        updatedPriceRange.prices = prices.map(({ id, packaging, price }) => {
          // Se esiste già un prezzo con questo ID, mantieni gli altri listini
          const existingPrice = existingPrices.find((p) => p.id === id);
          if (existingPrice) {
            return {
              ...existingPrice,
              packaging,
              [pricelistId]: price,
            };
          }
          // Altrimenti crea un nuovo prezzo
          return {
            id,
            packaging,
            [pricelistId]: price,
          };
        });

        // Aggiorna il documento
        productData.priceranges[priceRangeIndex] = updatedPriceRange;

        await productRef.update({
          priceranges: productData.priceranges,
        });

        // Ricarica i prodotti per aggiornare la vista
        await this.$store.dispatch('products/INIT_PRODUCTS');

        console.log('Prezzi aggiornati con successo'); // eslint-disable-line no-console
      } catch (error) {
        console.error('Errore durante il salvataggio dei prezzi:', error); // eslint-disable-line no-console
      }
    },

    getProductPriceRanges(product) {
      // Restituisce tutte le fasce di prezzo con i relativi packaging
      if (!product.priceranges || product.priceranges.length === 0) {
        return [];
      }

      const rows = [];
      product.priceranges.forEach((priceRange, priceRangeIndex) => {
        const priceRangeName = getLocalizedPriceRangeName(priceRange, this.userLanguage) || 'Fascia standard';
        const isFirstPriceRange = priceRangeIndex === 0;
        const previousPriceRangeName = priceRangeIndex > 0
          ? getLocalizedPriceRangeName(product.priceranges[priceRangeIndex - 1], this.userLanguage) || 'Fascia standard'
          : null;
        const isFirstInGroup = isFirstPriceRange || previousPriceRangeName !== priceRangeName;
        let isFirstRowInRange = true;

        if (priceRange.prices && priceRange.prices.length > 0) {
          // Fascia con prezzi esistenti: mostra tutti i packaging
          priceRange.prices.forEach((p, packagingIndex) => {
            // Usa getPriceForPricelist per supportare listini derivati
            const price = getPriceForPricelist(
              product,
              priceRangeIndex,
              packagingIndex,
              this.pricelist,
              this.allPricelists,
            );

            rows.push({
              priceRangeName,
              priceRangeIndex,
              packaging: p.packaging,
              price,
              isFirstInGroup: isFirstInGroup && isFirstRowInRange,
              isFirstRowInRange,
            });
            isFirstRowInRange = false;
          });
        } else {
          // Fascia senza prezzi: mostra una riga vuota per permettere l'aggiunta del primo packaging
          rows.push({
            priceRangeName,
            priceRangeIndex,
            packaging: null,
            price: null,
            isFirstInGroup: isFirstInGroup && isFirstRowInRange,
            isFirstRowInRange,
          });
        }
      });

      return rows;
    },

    formatPackaging(packaging, um) {
      if (packaging === null || packaging === undefined || packaging === '') {
        return '—';
      }
      return `${packaging} ${this.unitLongPlural(um)}`;
    },

    unitLongPlural(um) {
      switch (um) {
        case 'kg': return this.$t('products.details.units.kilograms');
        case 'l': return this.$t('products.details.units.liters');
        case 'mq': return this.$t('products.details.units.squareMeter');
        case 'pz': return this.$t('products.details.units.pieces');
        default: return '';
      }
    },

    formatPrice(price) {
      if (price === null || price === undefined || price === 0) {
        return '—';
      }
      return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR',
      }).format(price);
    },
  },

  beforeDestroy() {
    // Cleanup timeout
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
  },
};
</script>

<style scoped>
/* Righe prodotti non disponibili con sfondo grigio chiaro */
::v-deep .grey.lighten-4 {
  background-color: #fafafa !important;
}

/* Allineamento verticale celle tabella */
::v-deep .v-data-table td {
  vertical-align: top;
}

/* Separatore tra fasce di prezzo diverse */
.price-range-separator {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  margin: 4px 0;
  width: 100%;
}

/* Evita il wrap del nome fascia per mantenere l'allineamento delle colonne */
.price-range-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
